import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  PencilIcon,
  TrashIcon,
  ClockIcon,
  ChatBubbleOvalLeftIcon,
  EllipsisHorizontalIcon,
  FaceSmileIcon,
} from '@heroicons/react/24/outline'
import { Button, Text } from '../../ui'
import { SocialPost, SOCIAL_POST_MAX_LENGTH } from '../../../types/social'
import AuthorAvatar from './AuthorAvatar'
import CommentList from './CommentList'
import EditHistoryModal from './EditHistoryModal'
import ReactionBar, { ReactionSummary } from './ReactionBar'
import { ReactionEmojiIcon, normalizeReactionEmoji } from './ReactionEmoji'
import MentionText from './MentionText'
import MentionTextarea from './MentionTextarea'
import PostImages from './PostImages'
import { feedCardClass, formatSocialDate } from './socialUtils'

export interface PostCardProps {
  post: SocialPost
  currentUserId?: string
  currentUserName?: string | null
  currentUserAvatar?: string | null
  moderation?: boolean
  embedded?: boolean
  onUpdate: (postId: string, content: string) => void | Promise<void>
  onDelete: (postId: string) => void | Promise<void>
  onReact: (postId: string, emoji: string) => void | Promise<void>
  onAddComment: (postId: string, content: string) => void | Promise<void>
  onUpdateComment: (commentId: string, content: string) => void | Promise<void>
  onDeleteComment: (commentId: string) => void | Promise<void>
  commentsOpen?: boolean
  onCommentsOpenChange?: (open: boolean) => void
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  currentUserId,
  currentUserName,
  currentUserAvatar,
  moderation = false,
  embedded = false,
  onUpdate,
  onDelete,
  onReact,
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  commentsOpen,
  onCommentsOpenChange,
}) => {
  const [editing, setEditing] = useState(false)
  const [editDraft, setEditDraft] = useState(post.content)
  const [submitting, setSubmitting] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [internalShowComments, setInternalShowComments] = useState(false)
  const [showReactions, setShowReactions] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const isCommentsControlled = onCommentsOpenChange !== undefined
  const showComments = isCommentsControlled ? (commentsOpen ?? false) : internalShowComments

  const setShowComments = (next: boolean) => {
    if (isCommentsControlled) {
      onCommentsOpenChange(next)
    } else {
      setInternalShowComments(next)
    }
  }

  const isOwner = post.author_id === currentUserId
  const canEdit = isOwner
  const canDelete = isOwner || moderation
  const reactions = post.reactions || []
  const comments = post.comments || []
  const commentCount = comments.length

  const userReaction = useMemo(() => {
    const emoji = reactions.find((r) => r.user_id === currentUserId)?.emoji ?? null
    return emoji ? normalizeReactionEmoji(emoji) : null
  }, [reactions, currentUserId])

  const handleSaveEdit = async () => {
    const trimmed = editDraft.trim()
    if (!trimmed && !(post.images?.length)) return
    try {
      setSubmitting(true)
      setActionError(null)
      await onUpdate(post.id, trimmed)
      setEditing(false)
      setShowMenu(false)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao editar post')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Excluir esta postagem?')) return
    try {
      setActionError(null)
      await onDelete(post.id)
    } catch (err) {
      setActionError(
        err instanceof Error
          ? err.message
          : err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string'
            ? (err as { message: string }).message
            : 'Erro ao excluir post'
      )
    }
  }

  const handleReact = async (emoji: string) => {
    try {
      setSubmitting(true)
      setActionError(null)
      await onReact(post.id, emoji)
      setShowReactions(false)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao reagir')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <article className={embedded ? 'bg-transparent' : feedCardClass}>
      {/* Cabeçalho */}
      <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2">
        <div className="flex gap-3 min-w-0">
          <AuthorAvatar
            name={post.author?.name}
            avatarPath={post.author?.avatar_url}
          />
          <div className="min-w-0 pt-0.5">
            <Text size="sm" className="font-semibold text-gray-900 dark:text-gray-100 leading-tight">
              {post.author?.name || 'Usuário'}
            </Text>
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-0.5">
              <Text size="xs" color="muted">{formatSocialDate(post.created_at)}</Text>
              {post.edited_at && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <button
                    type="button"
                    onClick={() => setShowHistory(true)}
                    className="inline-flex items-center gap-0.5 text-xs text-gray-500 hover:text-primary-600 dark:hover:text-primary-400"
                  >
                    <ClockIcon className="h-3 w-3" />
                    Editado
                  </button>
                </>
              )}
              {moderation && !isOwner && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span className="text-xs text-amber-600 dark:text-amber-400">Moderação</span>
                </>
              )}
            </div>
          </div>
        </div>

        {(canEdit || canDelete) && !editing && (
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowMenu((v) => !v)}
              className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Opções da publicação"
            >
              <EllipsisHorizontalIcon className="h-5 w-5" />
            </button>
            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                  aria-hidden
                />
                <div className="absolute right-0 top-full mt-1 z-20 min-w-[8rem] rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg py-1">
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(true)
                        setEditDraft(post.content)
                        setShowMenu(false)
                        setActionError(null)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                    >
                      <PencilIcon className="h-4 w-4" />
                      Editar
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowMenu(false)
                        handleDelete()
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Excluir
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Conteúdo */}
      <div className="px-4 pb-3">
        {editing ? (
          <div className="space-y-2">
            <MentionTextarea
              value={editDraft}
              onChange={setEditDraft}
              maxLength={SOCIAL_POST_MAX_LENGTH}
              rows={3}
              disabled={submitting}
            />
            <PostImages images={post.images} />
            <div className="flex gap-2">
              <Button
                variant="solid"
                disabled={submitting || (!editDraft.trim() && !(post.images?.length))}
                onClick={handleSaveEdit}
              >
                Salvar
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(false)
                  setEditDraft(post.content)
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {post.content.trim() ? (
              <p className="text-[15px] leading-relaxed text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                <MentionText content={post.content} />
              </p>
            ) : null}
            <PostImages images={post.images} />
          </div>
        )}
      </div>

      {/* Resumo de engajamento */}
      {(reactions.length > 0 || commentCount > 0) && (
        <div className="flex items-center justify-between gap-2 px-4 pb-2 text-xs">
          <ReactionSummary reactions={reactions} iconSize="md" />
          {commentCount > 0 && (
            <button
              type="button"
              onClick={() => setShowComments(true)}
              className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:underline"
            >
              {commentCount} {commentCount === 1 ? 'comentário' : 'comentários'}
            </button>
          )}
        </div>
      )}

      <div className="border-t border-gray-200 dark:border-gray-700 mx-4" />

      {/* Barra de ações */}
      <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
        <button
          type="button"
          disabled={submitting}
          onClick={() => {
            const next = !showReactions
            setShowReactions(next)
            if (next) setShowComments(false)
          }}
          className={clsx(
            'flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors',
            'hover:bg-gray-50 dark:hover:bg-gray-700/40',
            showReactions || userReaction
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-gray-600 dark:text-gray-400',
            showReactions && 'bg-primary-50/50 dark:bg-primary-900/20'
          )}
        >
          {userReaction ? (
            <ReactionEmojiIcon emoji={userReaction} size="md" />
          ) : (
            <>
              <FaceSmileIcon className="h-5 w-5" />
              <span>Reagir</span>
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            const next = !showComments
            setShowComments(next)
            if (next) setShowReactions(false)
          }}
          className={clsx(
            'flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors',
            'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/40',
            showComments && 'text-primary-600 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/20'
          )}
        >
          <ChatBubbleOvalLeftIcon className="h-5 w-5" />
          Comentar
        </button>
      </div>

      {showReactions && (
        <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700/80 bg-gray-50/50 dark:bg-gray-900/20">
          <ReactionBar
            reactions={reactions}
            currentUserId={currentUserId}
            disabled={submitting}
            variant="actions"
            onReact={handleReact}
          />
        </div>
      )}

      {showComments && (
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/80 bg-gray-50/30 dark:bg-gray-900/10">
          <CommentList
            comments={comments}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            currentUserAvatar={currentUserAvatar}
            moderation={moderation}
            onAdd={(content) => onAddComment(post.id, content)}
            onUpdate={onUpdateComment}
            onDelete={onDeleteComment}
          />
        </div>
      )}

      {actionError && (
        <div className="px-4 pb-3">
          <Text size="xs" className="text-red-600 dark:text-red-400">{actionError}</Text>
        </div>
      )}

      {showHistory && (
        <EditHistoryModal
          title="Histórico da postagem"
          postId={post.id}
          currentContent={post.content}
          onClose={() => setShowHistory(false)}
        />
      )}
    </article>
  )
}

export default PostCard
